import Papa from "papaparse";

const USER_HINTS = ["user", "picker", "operator", "employee", "staff", "login", "worker", "agent", "name", "id"];
const DATE_HINTS = ["date", "timestamp", "datetime", "time", "created", "day", "visit"];
const QTY_HINTS = ["act qty", "actual", "picked", "qty", "quantity", "picked", "units", "pieces", "pcs", "count", "amount", "volume"];
const TIME_HINTS = ["time", "clock", "confirmed"];
const HOP_HINTS = ["hoptime", "hop", "duration", "elapsed", "interval"];
const MONTHS = { jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6, jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12 };
const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const hintScore = (h, hints) => {
  const l = h.toLowerCase().trim();
  if (hints.includes(l)) return 150;
  return hints.reduce((s, k, i) => (l.includes(k) ? Math.max(s, 100 - i) : s), 0);
};

const isNum = (v) => v !== "" && v != null && !isNaN(Number(String(v).replace(/,/g, "")));

export function previewFile(file) {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      preview: 200,
      skipEmptyLines: true,
      complete: (res) => {
        const [headers = [], ...rows] = res.data;
        if (!headers.length) return reject(new Error("No header row found"));
        resolve({ headers: headers.map((h, i) => String(h).trim() || `Column ${i + 1}`), rows });
      },
      error: reject,
    });
  });
}

export function detectDateFormat(rows, idx) {
  let dmy = 0, mdy = 0;
  for (const r of rows) {
    const p = String(r[idx] ?? "").trim().split(/[/\-.\sT:,]+/);
    if (p.length < 3 || p[0].length === 4 || !/^\d+$/.test(p[0]) || !/^\d+$/.test(p[1])) continue;
    if (+p[0] > 12) dmy++;
    if (+p[1] > 12) mdy++;
  }
  return dmy > mdy ? "DMY" : "MDY";
}

const pad = (n) => String(n).padStart(2, "0");

export function dayKey(raw, fmt) {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (!s) return null;
  const p = s.split(/[/\-.\sT:,]+/);
  let y, m, d;
  if (p.length >= 3) {
    if (p[0].length === 4 && /^\d+$/.test(p[0])) {
      y = +p[0];
      m = /^\d+$/.test(p[1]) ? +p[1] : MONTHS[p[1].slice(0, 3).toLowerCase()];
      d = +p[2];
    } else if (/^\d+$/.test(p[0]) && /^\d+$/.test(p[1])) {
      y = +p[2];
      m = fmt === "DMY" ? +p[1] : +p[0];
      d = fmt === "DMY" ? +p[0] : +p[1];
      if (m > 12 && d <= 12) [m, d] = [d, m];
    } else {
      const mi = MONTHS[p[0].slice(0, 3).toLowerCase()] ? 0 : 1;
      m = MONTHS[p[mi].slice(0, 3).toLowerCase()];
      y = +(p.find((x) => /^\d{4}$/.test(x)) || NaN);
      d = +(p.find((x, i) => i !== mi && /^\d{1,2}$/.test(x)) || 1);
    }
    if (y < 100) y += 2000;
    if (y > 1900 && m >= 1 && m <= 12 && d >= 1 && d <= 31) return `${y}-${pad(m)}-${pad(d)}`;
  }
  if (/^\d{5}(\.\d+)?$/.test(s)) {
    const dt = new Date(Math.round((+s - 25569) * 86400000));
    return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`;
  }
  const t = /[a-z]/i.test(s) ? Date.parse(s) : NaN;
  if (!isNaN(t)) {
    const dt = new Date(t);
    return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
  }
  return null;
}

export const monthKey = (raw, fmt) => dayKey(raw, fmt)?.slice(0, 7) ?? null;

const TIME_RE = /(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(am|pm)?/i;
export function timeSec(v) {
  const m = TIME_RE.exec(String(v ?? ""));
  if (!m) return null;
  let h = +m[1];
  if (m[4]) { const pm = m[4].toLowerCase() === "pm"; if (pm && h < 12) h += 12; if (!pm && h === 12) h = 0; }
  return h > 23 ? null : h * 3600 + +m[2] * 60 + +(m[3] || 0);
}

export function detectColumns(headers, rows) {
  const used = new Set();
  const pick = (hints, test) => {
    let best = -1, bestScore = -1;
    headers.forEach((h, i) => {
      if (used.has(i)) return;
      const samples = rows.slice(0, 50).map((r) => r[i]);
      const fit = samples.filter(test).length / Math.max(samples.length, 1);
      const sc = hintScore(h, hints) + fit * 50;
      if (fit > 0.5 && sc > bestScore) { best = i; bestScore = sc; }
    });
    if (best >= 0) used.add(best);
    return best;
  };
  const date = pick(DATE_HINTS, (v) => (!isNum(v) && monthKey(v, "MDY") !== null) || /^\d{5}$/.test(String(v)));
  const qty = pick(QTY_HINTS, isNum);
  const hop = pick(HOP_HINTS, (v) => parseHop(v) !== null && !isNum(v));
  const time = pick(TIME_HINTS, (v) => /^\s*\d{1,2}:\d{2}(:\d{2})?\s*(am|pm)?\s*$/i.test(String(v ?? "")));
  const user = pick(USER_HINTS, (v) => String(v ?? "").trim() !== "");
  const dateHasTime = date >= 0 && rows.slice(0, 20).some((r) => timeSec(r[date]) !== null);
  return {
    time: time >= 0 && hintScore(headers[time], TIME_HINTS) > 0 ? String(time) : dateHasTime ? "date" : "none",
    user: user >= 0 ? String(user) : "",
    date: date >= 0 ? String(date) : "",
    qty: qty >= 0 ? String(qty) : "none",
    hop: hop >= 0 && hintScore(headers[hop], HOP_HINTS) > 0 ? String(hop) : "none",
    dateFormat: date >= 0 ? detectDateFormat(rows, date) : "MDY",
  };
}

export const CAPS = [300, 600, 900, 1800, 3600];
const NC = CAPS.length;

export function parseHop(v) {
  const s = String(v ?? "").trim();
  if (!s) return null;
  const p = s.split(":");
  if (p.length === 3 && p.every((x) => /^\d+$/.test(x))) return +p[0] * 3600 + +p[1] * 60 + +p[2];
  if (p.length === 2 && p.every((x) => /^\d+$/.test(x))) return +p[0] * 60 + +p[1];
  return isNum(s) ? Number(s) : null;
}

export const MIN_HOP_SEC = 1800;
export const pph = (cell, ci) => (cell && cell[2 + ci] >= MIN_HOP_SEC ? cell[2 + NC + ci] / (cell[2 + ci] / 3600) : 0);
const r2 = (n) => Math.round(n * 100) / 100;

function fillMonths(min, max) {
  const out = [];
  let [y, m] = min.split("-").map(Number);
  const [ey, em] = max.split("-").map(Number);
  while (y < ey || (y === ey && m <= em)) {
    out.push(`${y}-${String(m).padStart(2, "0")}`);
    m++; if (m > 12) { m = 1; y++; }
  }
  return out;
}

export const monthLabel = (k) => `${MONTH_NAMES[+k.slice(5, 7) - 1]} ${k.slice(2, 4)}`;

export function analyzeFile(file, mapping, onProgress) {
  const ui = +mapping.user, di = +mapping.date;
  const qi = mapping.qty === "none" ? -1 : +mapping.qty;
  const hi = !mapping.hop || mapping.hop === "none" ? -1 : +mapping.hop;
  const fmt = mapping.dateFormat;
  const tc = !mapping.time || mapping.time === "none" ? -1 : mapping.time === "date" ? di : +mapping.time;
  const users = new Map();
  const days = new Map();
  let rowsRead = 0, skipped = 0, headerSkipped = false;
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      skipEmptyLines: true,
      chunkSize: 1024 * 1024 * 4,
      chunk: (res) => {
        const data = res.data;
        for (let r = 0; r < data.length; r++) {
          if (!headerSkipped) { headerSkipped = true; continue; }
          const row = data[r];
          rowsRead++;
          const uid = String(row[ui] ?? "").trim();
          const dk = dayKey(row[di], fmt);
          if (!uid || !dk) { skipped++; continue; }
          const mk = dk.slice(0, 7);
          let q = 0;
          if (qi >= 0) { const n = parseFloat(String(row[qi] ?? "").replace(/,/g, "")); if (!isNaN(n)) q = n; }
          let monthly = users.get(uid);
          if (!monthly) { monthly = {}; users.set(uid, monthly); }
          const cell = monthly[mk] || (monthly[mk] = new Array(2 + NC * 2).fill(0));
          cell[0]++; cell[1] += q;
          const h = hi >= 0 ? parseHop(row[hi]) : null;
          if (h !== null) for (let c = 0; c < NC; c++) if (h <= CAPS[c]) { cell[2 + c] += h; cell[2 + NC + c]++; }
          const ts = tc >= 0 ? timeSec(row[tc]) : null;
          if (ts !== null) {
            let ud = days.get(uid);
            if (!ud) { ud = {}; days.set(uid, ud); }
            const dd = ud[dk] || (ud[dk] = [0, 0, 1e9, -1, []]);
            dd[0]++; dd[1] += q;
            if (ts < dd[2]) dd[2] = ts;
            if (ts > dd[3]) dd[3] = ts;
            dd[4].push(ts);
          }
        }
        onProgress?.(Math.min(99, Math.round((res.meta.cursor / file.size) * 100)), rowsRead);
      },
      complete: () => {
        const all = new Set();
        users.forEach((m) => Object.keys(m).forEach((k) => all.add(k)));
        const keys = [...all].sort();
        if (!keys.length) return reject(new Error("No valid rows found. Check your column mapping and date format."));
        const dayOut = {};
        days.forEach((ud, uid) => {
          const o = (dayOut[uid] = {});
          for (const [k, d] of Object.entries(ud)) {
            const t = Int32Array.from(d[4]).sort();
            const gaps = [];
            for (let i = 1; i < t.length; i++) if (t[i] - t[i - 1] >= 300) gaps.push([t[i - 1], t[i] - t[i - 1]]);
            o[k] = [d[0], r2(d[1]), d[2], d[3], gaps];
          }
        });
        resolve({ days: days.size ? dayOut : null, hasTime: days.size > 0, rowsRead, skipped, hasQty: qi >= 0, hasHop: hi >= 0, months: fillMonths(keys[0], keys[keys.length - 1]), users: [...users].map(([id, monthly]) => ({ id, monthly })) });
      },
      error: reject,
    });
  });
}

const addCell = (t, c) => { for (let i = 0; i < c.length; i++) t[i] += c[i]; };

export function applyRange(raw, from, to, capIdx) {
  const months = raw.months.filter((k) => k >= from && k <= to);
  const totals = {};
  const users = [];
  for (const u of raw.users) {
    const keys = Object.keys(u.monthly).filter((k) => k >= from && k <= to).sort();
    if (!keys.length) continue;
    const sum = new Array(2 + NC * 2).fill(0);
    for (const k of keys) {
      addCell(sum, u.monthly[k]);
      addCell(totals[k] || (totals[k] = new Array(2 + NC * 2).fill(0)), u.monthly[k]);
    }
    users.push({ id: u.id, monthly: u.monthly, visits: sum[0], qty: r2(sum[1]), avg: sum[0] ? sum[1] / sum[0] : 0, pph: pph(sum, capIdx), first: keys[0], last: keys[keys.length - 1], activeMonths: keys.length });
  }
  users.sort((a, b) => b.visits - a.visits);
  const grand = new Array(2 + NC * 2).fill(0);
  const overall = months.map((k) => {
    const t = totals[k];
    if (t) addCell(grand, t);
    return { month: k, label: monthLabel(k), visits: t?.[0] || 0, qty: r2(t?.[1] || 0), pph: r2(pph(t, capIdx)) };
  });
  return {
    rowsRead: raw.rowsRead, skipped: raw.skipped, hasQty: raw.hasQty, hasHop: raw.hasHop, capIdx,
    months, overall, users, totalVisits: grand[0], totalQty: grand[1], totalPph: pph(grand, capIdx),
    hopCoverage: grand[0] ? grand[2 + NC + capIdx] / grand[0] : 0,
  };
}

export function userSeries(months, users, metric, capIdx = 0) {
  return months.map((k) => {
    const row = { month: k, label: monthLabel(k) };
    users.forEach((u) => {
      const c = u.monthly[k];
      row[u.id] = r2(metric === "pph" ? pph(c, capIdx) : c?.[metric === "qty" ? 1 : 0] || 0);
    });
    return row;
  });
}

export function topUsersForMonth(users, month, n = 8) {
  return users
    .filter((u) => u.monthly[month])
    .map((u) => ({ id: u.id, visits: u.monthly[month][0], qty: u.monthly[month][1] }))
    .sort((a, b) => b.visits - a.visits)
    .slice(0, n);
}

const hopStr = (sec) => { const t = Math.round(sec); return [t / 3600, (t % 3600) / 60, t % 60].map((x) => String(Math.floor(x)).padStart(2, "0")).join(":"); };

export function makeDemoFile(rows = 250000) {
  const names = ["Alice Morgan", "Ben Carter", "Chloe Diaz", "Dev Patel", "Ella Novak", "Finn Walsh", "Grace Lee", "Hugo Brandt", "Isla Reid", "Jay Kim", "Kara Holt", "Leo Rossi", "Maya Singh", "Noah Price", "Olive Grant", "Pete Young", "Quinn Ford", "Ruby Shaw", "Sam Ortiz", "Tara Bloom", "Umar Aziz", "Vera Lund", "Will Hart", "Xena Cole", "Yusuf Ali", "Zoe Marsh"];
  const weights = names.map((_, i) => 1 + ((i * 37) % 11) / 4);
  const totalW = weights.reduce((a, b) => a + b, 0);
  const lines = ["Picker Name,Visit Date,Location,Qty Picked,Hoptime"];
  for (let i = 0; i < rows; i++) {
    let r = Math.random() * totalW, ui = 0;
    while (r > weights[ui]) { r -= weights[ui]; ui++; }
    const mOff = Math.floor(Math.pow(Math.random(), 0.8) * 24);
    const y = 2024 + Math.floor(mOff / 12), m = (mOff % 12) + 1;
    const d = 1 + Math.floor(Math.random() * 28);
    const seasonal = m >= 10 ? 1.6 : 1;
    const qty = Math.max(1, Math.round((2 + Math.random() * 10) * seasonal * (1 + (ui % 5) / 5)));
    lines.push(`${names[ui]},${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")} ${String(6 + (i % 12)).padStart(2, "0")}:15,A${(i % 40) + 1}-${i % 9},${qty},${hopStr(Math.random() < 0.01 ? 900 + Math.random() * 5000 : Math.random() * (30 + (ui % 7) * 12))}`);
  }
  return new File([lines.join("\n")], "demo_picking_250k.csv", { type: "text/csv" });
}
